/**
 * Слоты нижней навигации — данными, а не разметкой.
 *
 * ЗАЧЕМ. До 2026-07-28 список вкладок был литералом внутри Layout.tsx: сам
 * массив, выбор фокус-слота и решение «что показывать» жили вперемешку с
 * версткой шапки, чата и баннеров на 346 строк. Поменять раскладку значило
 * править экран, а проверить раскладку — отрендерить его целиком со всеми
 * хуками (useMe, useGoal, useUnread, useTheme, Telegram…). У соседней
 * admin-аппы то же самое давно лежит конфигом (`items[]` + `visibleItems()`),
 * и правится там одной строкой.
 *
 * ЧТО ЭТО НЕ МЕНЯЕТ. Раскладка перенесена ОДИН В ОДИН: пять слотов, третий —
 * адаптивный «Фокус» по фазе сезона. Это шаг структуры, не дизайна.
 *
 * ЛЕЙБЛЫ — КЛЮЧАМИ. Конфиг хранит `labelKey`, а не текст: t() зависит от
 * инстанса i18n и языка, и утащив его сюда, мы бы получили модуль, который
 * нельзя проверить без поднятия локализации.
 */

import type { FocusPhase } from "./lib/focusTab";
import {
  IconChart,
  IconHome,
  IconMore,
  IconPlan,
  IconPulse,
  type NavIcon,
} from "./components/navIcons";

export interface NavSlot {
  /** Стабильный идентификатор слота — для тестов и ключей React. */
  key: string;
  to: string;
  /** Ключ внутри неймспейса `nav`. */
  labelKey: string;
  /** Запасной текст, если ключа в локали нет. */
  labelFallback?: string;
  icon: NavIcon;
}

export interface NavContext {
  /** Фаза сезона: нет цели → Старты, цель активна → План, гонка близко → Гонка. */
  focusPhase: FocusPhase;
}

/**
 * Слот «План» ведёт в разное место по фазе сезона: пока цели нет — в каталог
 * стартов (искать не план, а повод), в race week — в центр гонки, который на
 * эти дни и есть весь план. Резолвер тот же (`resolveFocusPhase`), но он
 * больше не двигает ОТДЕЛЬНЫЙ слот — только адресата внутри «Плана».
 * Мышечная память цела: третья позиция всегда «План».
 */
const PLAN_TARGET: Record<FocusPhase, string> = {
  events: "/events",     // цели нет — сначала найти старт
  plan: "/plan",         // цель есть — неделя и цикл
  race: "/race-week",    // старт близко — центр гонки
};

/**
 * Слоты в порядке отрисовки, слева направо.
 *
 * Раскладка принята 2026-07-28 (MOBILE_NAV_SPEC.md) — гибрид концепций
 * «три времени» и «верстак»: день · будущее · состояние сейчас · прошлое ·
 * всё прочее. Пять слотов ОДИНАКОВЫ для Solo и Coached: сильный атлет
 * остаётся сильным атлетом и с тренером, и без, а разница живёт внутри
 * экранов, не в структуре панели. Чат — side-sheet в шапке (канон: side-sheet в шапке).
 *
 * Ключевое различие с прежней раскладкой: «Форма» и «Верстак» разъехались.
 * Форма — состояние на сегодня, читается за пять секунд утром. Верстак —
 * прошлое и его анализ, туда идут осознанно. Раньше и то и другое лежало в
 * одной «Аналитике», и чтобы узнать, выспался ли ты, приходилось
 * продираться сквозь годовые графики.
 */
export function resolveNavSlots(ctx: NavContext): NavSlot[] {
  return [
    { key: "today", to: "/", labelKey: "today", labelFallback: "Сегодня", icon: IconHome },
    { key: "plan", to: PLAN_TARGET[ctx.focusPhase], labelKey: "plan", icon: IconPlan },
    { key: "form", to: "/form", labelKey: "form", labelFallback: "Форма", icon: IconPulse },
    // «Анализ», а не «Верстак»: верстак — метафора мастерской, атлет так не
    // говорит. Слово должно быть тем, которым он пользуется в жизни, и
    // коротким — при пяти слотах длинный ярлык не помещается.
    { key: "analysis", to: "/analytics/studio", labelKey: "analysis", labelFallback: "Анализ", icon: IconChart },
    { key: "more", to: "/more", labelKey: "more.tab", labelFallback: "Ещё", icon: IconMore },
  ];
}
